import { ApolloError, useMutation, useQuery } from '@apollo/client';
import {
	Button,
	Chip,
	SettingsGroup,
	SettingsItemCheckbox,
	SettingsItemTextField,
	Tooltip,
} from '@elipssolution/harfang';
import { mdiPencil, mdiPlus } from '@mdi/js';
import Icon from '@mdi/react';
import { Typography, styled } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { useConfirmationDialogContext } from '../../../../../components/ConfirmationDialogProvider';
import SettingsDialogPage from '../../../../../components/SettingsDialogPage';
import { useSettingsDialog } from '../../../../../hooks/useSettingsDialog';
import { generateErrorInformations } from '../../../../../utils/errorHandler';
import { SUCCESS_ANIMATION_DELAY } from '../../../../../utils/statusAnimationDelay';
import {
	CREATE_STORAGE_UPLOAD,
	CreateStorageUploadType,
	FETCH_STORAGE_UPLOAD,
	FetchStorageUploadType,
	REMOVE_STORAGE_UPLOAD,
	RemoveStorageUploadType,
	UPDATE_STORAGE_UPLOAD,
	UpdateStorageUploadType,
} from '../../../api/settingStorageUpload';
import ConnectorForm, { ConnectorFormType } from '../../../components/ConnectorForm';
import { SettingStorageUploadType } from '../../../types/settingStorage';

const TagsWrapper = styled('div')(({ theme }) => ({
	display: 'flex',
	flexWrap: 'wrap',
	gap: theme.spacing(1),
}));

const ActionsWrapper = styled('div')({
	display: 'flex',
	flexDirection: 'row-reverse',
	justifyContent: 'space-between',
});

const ErrorWrapper = styled('div')(({ theme }) => ({
	height: 36,
	width: '50%',

	display: 'grid',
	placeItems: 'center',

	color: theme.palette.error.main,
	backgroundColor: `${theme.palette.error.main}1A`,
	borderRadius: theme.shape.borderRadius,
}));

type StorageUploadFormProps = {
	storageUpload?: SettingStorageUploadType;
};
type StorageUploadFormType = {
	isImmediatelyProcessed: boolean;
	name: string;
};

const StorageUploadForm = ({ storageUpload: initialStorageUpload }: StorageUploadFormProps) => {
	const { name: initialStorageUploadName } = initialStorageUpload ?? {};

	const { back, onHasDirtyFormChange, onCloseConfirmationTitleChange } = useSettingsDialog();
	const { confirmAction } = useConfirmationDialogContext();

	const {
		control,
		formState: { isDirty, isValid },
		handleSubmit,
		reset,
	} = useForm<StorageUploadFormType>();

	const [title, setTitle] = useState(initialStorageUploadName);
	const [storageUpload, setStorageUpload] = useState(initialStorageUpload);

	const [error, setError] = useState<string>();
	const [connectorFormValues, setConnectorFormValues] = useState<ConnectorFormType>();
	const [isConfigurationValid, setIsConfigurationValid] = useState(false);
	const [isCreateStorageUploadSucceeded, setIsCreateStorageUploadSucceeded] = useState(false);
	const [isUpdateStorageUploadSucceeded, setIsUpdateStorageUploadSucceeded] = useState(false);
	const [isConnectorFormDirty, setIsConnectorFormDirty] = useState(false);

	const applyStorageUploadToForm = useCallback(
		({ isImmediatelyProcessed, name }: SettingStorageUploadType) => {
			reset({
				isImmediatelyProcessed,
				name,
			});
		},
		[reset],
	);

	const { data: storageUploadData, loading: isStorageUploadFetchLoading } = useQuery<FetchStorageUploadType>(
		FETCH_STORAGE_UPLOAD,
		{
			onCompleted: ({ document_settingStorageUpload }) => applyStorageUploadToForm(document_settingStorageUpload),
			onError: (fetchError) =>
				setError(generateErrorInformations({ error: fetchError, resource: 'document_settingStorageUpload' }).message),
			skip: !storageUpload,
			variables: { id: storageUpload?.id },
		},
	);

	const connectorInitialFormValues = useMemo(() => {
		if (storageUploadData?.document_settingStorageUpload?.connector)
			return {
				configuration: JSON.parse(storageUploadData?.document_settingStorageUpload.configuration ?? '{}') as Record<
					string,
					string
				>,
				connector: storageUploadData?.document_settingStorageUpload.connector,
			};

		return undefined;
	}, [storageUploadData]);

	const { tags } = useMemo(
		() => storageUploadData?.document_settingStorageUpload ?? { name: undefined, tags: [] },
		[storageUploadData],
	);

	const hasTags = useMemo(() => tags.length !== 0, [tags]);

	const [createStorageUpload, { loading: isCreateStorageUploadLoading }] = useMutation<CreateStorageUploadType>(
		CREATE_STORAGE_UPLOAD,
		{
			onCompleted: ({ document_createSettingStorageUpload }) => {
				setStorageUpload(document_createSettingStorageUpload);
				setIsCreateStorageUploadSucceeded(true);
				setTimeout(() => setIsCreateStorageUploadSucceeded(false), SUCCESS_ANIMATION_DELAY);
			},
		},
	);

	const [updateStorageUpload, { loading: isUpdateStorageUploadLoading }] = useMutation<UpdateStorageUploadType>(
		UPDATE_STORAGE_UPLOAD,
		{
			onCompleted: () => {
				setIsUpdateStorageUploadSucceeded(true);
				setTimeout(() => setIsUpdateStorageUploadSucceeded(false), SUCCESS_ANIMATION_DELAY);
			},
		},
	);

	const [removeStorageUpload, { data: removeStorageUploadMutationData, loading: isRemoveStorageUploadLoading }] =
		useMutation<RemoveStorageUploadType>(REMOVE_STORAGE_UPLOAD, {
			onCompleted: back,
			onError: (mutationError) =>
				generateErrorInformations({ error: mutationError, resource: 'document_removeSettingStorageUpload' }).message,
		});

	const isLoading = useMemo(
		() =>
			isStorageUploadFetchLoading ||
			isCreateStorageUploadLoading ||
			isUpdateStorageUploadLoading ||
			isRemoveStorageUploadLoading,
		[
			isStorageUploadFetchLoading,
			isCreateStorageUploadLoading,
			isUpdateStorageUploadLoading,
			isRemoveStorageUploadLoading,
		],
	);

	const isMutationButtonDisabled = useMemo(
		() => isLoading || !(isValid || isConfigurationValid) || !(isDirty || isConnectorFormDirty),
		[isConfigurationValid, isConnectorFormDirty, isDirty, isLoading, isValid],
	);

	const submitButtonLabel = useMemo(() => {
		if (storageUpload) {
			if (isCreateStorageUploadSucceeded) return 'Emplacement ajouté';
			if (isUpdateStorageUploadSucceeded) return 'Emplacement modifié';

			return 'Modifier';
		}

		return 'Ajouter';
	}, [isCreateStorageUploadSucceeded, isUpdateStorageUploadSucceeded, storageUpload]);

	const onSubmit = useCallback(
		(values: StorageUploadFormType) => {
			const { configuration, connector: connectorValue } = connectorFormValues ?? {};
			const storageUploadFormData = {
				...values,
				...(Object.keys(configuration || {}).length && { configuration: JSON.stringify(configuration) }),
				connector: connectorValue,
			};

			const modeMutationInfoMap = new Map([
				[
					'create',
					{
						mutation: createStorageUpload,
						mutationName: 'createSettingStorageUpload',
						inputValues: storageUploadFormData,
					},
				],
				[
					'update',
					{
						mutation: updateStorageUpload,
						mutationName: 'updateSettingStorageUpload',
						inputValues: {
							...storageUploadFormData,
							id: storageUpload?.id,
						},
					},
				],
			]);

			const {
				mutation,
				mutationName = '',
				inputValues,
			} = modeMutationInfoMap.get(storageUpload?.id ? 'update' : 'create') || {};

			return mutation?.({
				variables: {
					[`${mutationName}Input`]: inputValues,
				},
			})
				.then(({ data }) => {
					if (!data) return;

					const { name } = values;

					setTitle(name);
					reset(values);
					setError(undefined);
					setIsConnectorFormDirty(false);
				})
				.catch((mutationError: ApolloError) =>
					setError(
						generateErrorInformations({
							error: mutationError,
							resource: storageUpload?.id ? 'updateSettingStorageUpload' : 'createSettingStorageUpload',
						}).message,
					),
				);
		},
		[connectorFormValues, createStorageUpload, updateStorageUpload, storageUpload?.id, reset],
	);

	const startIcon = useMemo(() => <Icon path={storageUpload ? mdiPencil : mdiPlus} size="24px" />, [storageUpload]);

	const handleDeleteButtonClick = async () => {
		const isDeleteConfirmed = await confirmAction({
			title: 'Êtes-vous sûr de vouloir supprimer ce dépôt ?',
			confirmButtonDetails: {
				color: 'error',
				label: 'Supprimer',
			},
		});

		if (!isDeleteConfirmed) return null;

		return removeStorageUpload({
			variables: {
				id: storageUpload?.id,
			},
		});
	};

	useEffect(() => {
		onHasDirtyFormChange(isDirty || isConnectorFormDirty);
		onCloseConfirmationTitleChange(
			`Êtes-vous sûr de vouloir quitter la ${
				initialStorageUpload ? 'modification' : 'création'
			} de l'emplacement de dépôt ?`,
		);
	}, [initialStorageUpload, isConnectorFormDirty, isDirty, onCloseConfirmationTitleChange, onHasDirtyFormChange]);

	return (
		<SettingsDialogPage title={title ?? "Ajout d'un emplacement de dépôt"}>
			<SettingsGroup>
				<Controller
					control={control}
					render={({ field }) => (
						<SettingsItemTextField
							{...field}
							description="Nom de l'emplacement de dépôt."
							disabled={isStorageUploadFetchLoading}
							label="Nom"
							required
						/>
					)}
					rules={{ required: true }}
					name="name"
				/>
				<Controller
					control={control}
					defaultValue={false}
					render={({ field: { value, ...field } }) => (
						<SettingsItemCheckbox
							{...field}
							checked={value}
							description="Si coché, le document déposé est immédiatement traité."
							disabled={isStorageUploadFetchLoading}
							label="Traitement immédiat"
						/>
					)}
					name="isImmediatelyProcessed"
				/>
			</SettingsGroup>

			<ConnectorForm
				onValuesChange={(values) => {
					setConnectorFormValues(values);
					if (values !== connectorFormValues) setIsConnectorFormDirty(true);
				}}
				onConfigurationValidityChange={setIsConfigurationValid}
				initialValues={connectorInitialFormValues}
				type="upload"
			/>

			{tags.length > 0 && (
				<div>
					<Typography paddingLeft={1} variant="h6">
						Tags associés
					</Typography>
					<TagsWrapper>
						{tags.map(({ color, id, name: tagName }) => (
							<Chip key={id} color={color} label={tagName} />
						))}
					</TagsWrapper>
				</div>
			)}

			<ActionsWrapper>
				<Button
					disabled={isMutationButtonDisabled}
					onClick={handleSubmit(onSubmit)}
					startIcon={startIcon}
					variant="contained"
				>
					{submitButtonLabel}
				</Button>

				{error && <ErrorWrapper>{error}</ErrorWrapper>}

				{storageUpload && (
					<Tooltip content={hasTags ? 'Un emplacement avec des tags associés ne peut pas être supprimé' : ''}>
						<Button color="error" disabled={isLoading || hasTags} onClick={handleDeleteButtonClick} variant="outlined">
							{removeStorageUploadMutationData ? 'Dépôt supprimé' : 'Supprimer'}
						</Button>
					</Tooltip>
				)}
			</ActionsWrapper>
		</SettingsDialogPage>
	);
};

export default StorageUploadForm;
